import React, { useState, useEffect } from "react";
import { Typography, Input, Button, Switch, Collapse } from "antd";
import {
  getStackingLimitSiteConfig,
  updateStackingLimitSiteConfig,
  updateOrderStatusChangeLimitSiteConfig,
  updateShiftStartWalletThresholdSiteConfig,
  updateSiteConfigCommonFunction,
} from "../services/siteConfig.service";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const WALLET_COMPLIANCE_SLUGS =
  "wallet_compliance_enforcement_enabled,wallet_compliance_rules,wallet_compliance_violation_threshold,wallet_compliance_max_threshold,wallet_compliance_allow_rider_end,wallet_compliance_allow_help_center_end,wallet_compliance_allow_api_end,wallet_clearance_thresholds";

const RADIUS_COMPLIANCE_SLUGS =
  "radius_compliance_enforcement_enabled,allow_help_center_end,allow_api_end";
  
const DEFAULT_CLEARANCE_BATCHES = ["1", "2", "3", "4", "5", "6"];

const createDefaultClearanceThresholds = () =>
  DEFAULT_CLEARANCE_BATCHES.map((batch) => ({
    batch,
    threshold: 0,
  }));

const normalizeLoadedClearanceThresholds = (raw) => {
  const defaults = createDefaultClearanceThresholds();
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }

  const byBatch = {};
  if (Array.isArray(list)) {
    list.forEach((item) => {
      const batch = String(item?.batch ?? item?.batchLevel ?? "").trim();
      if (!batch) return;
      const threshold = Number(item?.threshold);
      byBatch[batch] = Number.isFinite(threshold) && threshold >= 0 ? threshold : 0;
    });
  } else if (list && typeof list === "object") {
    Object.keys(list).forEach((batch) => {
      const threshold = Number(list[batch]);
      byBatch[String(batch)] =
        Number.isFinite(threshold) && threshold >= 0 ? threshold : 0;
    });
  }

  return defaults.map((row) => ({
    batch: row.batch,
    threshold: byBatch[row.batch] != null ? byBatch[row.batch] : 0,
  }));
};

const DEFAULT_COMPLIANCE_RULE = {
  threshold: 3,
  reason: "Wallet Compliance Hold",
  allow_rider_end: true,
  allow_help_center_end: true,
  allow_api_end: false,
};

const createEmptyRule = () => ({
  threshold: "",
  reason: "",
  allow_rider_end: true,
  allow_help_center_end: true,
  allow_api_end: false,
});

const normalizeLoadedRules = (rawRules) => {
  let list = rawRules;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list) || !list.length) return null;

  return list.map((rule) => ({
    threshold: rule?.threshold ?? "",
    reason: rule?.reason ?? "",
    allow_rider_end:
      rule?.allow_rider_end === true ||
      rule?.allow_rider_end === 1 ||
      rule?.allow_rider_end === "1",
    allow_help_center_end:
      rule?.allow_help_center_end === true ||
      rule?.allow_help_center_end === 1 ||
      rule?.allow_help_center_end === "1",
    allow_api_end:
      rule?.allow_api_end === true ||
      rule?.allow_api_end === 1 ||
      rule?.allow_api_end === "1",
  }));
};

const SiteConfig = () => {
  const [stackingLimit, setStackingLimit] = useState(null);
  const [otpLimit, setOtpLimit] = useState(null);
  const [orderStatusChangeLimit, setOrderStatusChangeLimit] = useState(null);
  const [shiftStartWalletThreshold, setShiftStartWalletThreshold] = useState(null);
  const [selfieRestrictShiftStart, setSelfieRestrictShiftStart] = useState(null);
  const [restrictRiderOnRegister, setRestrictRiderOnRegister] = useState(null);
  const [hideCustomerLocationOnApp, setHideCustomerLocationOnApp] = useState(null);
  const [walletComplianceEnabled, setWalletComplianceEnabled] = useState(false);
  const [radiusComplianceEnabled, setRadiusComplianceEnabled] = useState(false);
  const [radiusAllowHelpCenterEndComplianceEnabled, setRadiusAllowHelpCenterEndComplianceEnabled] = useState(false);
  const [radiusAllowApiEndComplianceEnabled, setRadiusAllowApiEndComplianceEnabled] = useState(false);
  
  const [walletComplianceRules, setWalletComplianceRules] = useState([
    { ...DEFAULT_COMPLIANCE_RULE },
  ]);
  const [walletClearanceThresholds, setWalletClearanceThresholds] = useState(
    createDefaultClearanceThresholds()
  );
  const [complianceActiveKeys, setComplianceActiveKeys] = useState(["conditions"]);
  const [loading, setLoading] = useState(false);
  const [Errors, setErrors] = useState({});
  const [postData, setPostData] = useState({ value: 0 });

  useEffect(() => {
    getConfig();
  }, []);

  useEffect(() => {
    if (postData && Array.isArray(postData)) {
      let enabled = false;
      let enabledRadius = false;
      let enabledRadiusallow_help_center_end = false;
      let enabledRadiusallow_api_end = false;
      let rulesFromConfig = null;
      let clearanceFromConfig = null;
      let legacyThreshold = 3;
      let legacyAllowRider = true;
      let legacyAllowHelpCenter = true;
      let legacyAllowApi = false;
      let hasLegacy = false;

      for (let i = 0; i < postData.length; i++) {
        if (postData[i].slug == "stacking_limit") {
          setStackingLimit(postData[i].value);
        } else if (postData[i].slug == "otp_limit") {
          setOtpLimit(postData[i].value);
        } else if (postData[i].slug == "order_status_change_limit") {
          setOrderStatusChangeLimit(postData[i].value);
        } else if (postData[i].slug == "shift_start_wallet_threshold") {
          setShiftStartWalletThreshold(postData[i].value);
        } else if (postData[i].slug == "selfie_restrict_shift_start") {
          setSelfieRestrictShiftStart(postData[i].value == 1 ? true : false);
        } else if (postData[i].slug == "restrict_rider_on_register") {
          setRestrictRiderOnRegister(postData[i].value == 1 ? true : false);
        } else if (postData[i].slug == "hide_customer_location_on_app") {
          setHideCustomerLocationOnApp(postData[i].value == 1 ? true : false);
        } else if (postData[i].slug == "radius_compliance_enforcement_enabled") {
          enabledRadius = postData[i].value == 1;
        } else if (postData[i].slug == "allow_help_center_end") {
          enabledRadiusallow_help_center_end = postData[i].value == 1;
        } else if (postData[i].slug == "allow_api_end") {
          enabledRadiusallow_api_end = postData[i].value == 1;
        } else if (postData[i].slug == "wallet_compliance_enforcement_enabled") {
          enabled = postData[i].value == 1;
        } else if (postData[i].slug == "wallet_compliance_rules") {
          rulesFromConfig = normalizeLoadedRules(postData[i].value);
        } else if (postData[i].slug == "wallet_clearance_thresholds") {
          clearanceFromConfig = normalizeLoadedClearanceThresholds(postData[i].value);
        } else if (postData[i].slug == "wallet_compliance_violation_threshold") {
          legacyThreshold = postData[i].value;
          hasLegacy = true;
        } else if (postData[i].slug == "wallet_compliance_allow_rider_end") {
          legacyAllowRider = postData[i].value == 1;
          hasLegacy = true;
        } else if (postData[i].slug == "wallet_compliance_allow_help_center_end") {
          legacyAllowHelpCenter = postData[i].value == 1;
          hasLegacy = true;
        } else if (postData[i].slug == "wallet_compliance_allow_api_end") {
          legacyAllowApi = postData[i].value == 1;
          hasLegacy = true;
        }
      }

      setWalletComplianceEnabled(enabled);
      setRadiusComplianceEnabled(enabledRadius);
      setRadiusAllowHelpCenterEndComplianceEnabled(enabledRadiusallow_help_center_end);
      setRadiusAllowApiEndComplianceEnabled(enabledRadiusallow_api_end);
      let nextRules;
      if (rulesFromConfig && rulesFromConfig.length) {
        nextRules = rulesFromConfig;
      } else if (hasLegacy) {
        nextRules = [
          {
            threshold: legacyThreshold || 3,
            reason: "Wallet Compliance Hold",
            allow_rider_end: legacyAllowRider,
            allow_help_center_end: legacyAllowHelpCenter,
            allow_api_end: legacyAllowApi,
          },
        ];
      } else {
        nextRules = [{ ...DEFAULT_COMPLIANCE_RULE }];
      }
      setWalletComplianceRules(nextRules);
      setWalletClearanceThresholds(
        clearanceFromConfig || createDefaultClearanceThresholds()
      );
      setComplianceActiveKeys(["conditions"]);
    }
  }, [postData]);

  const getConfig = async () => {
    try {
      setLoading(true);
      const resp = await getStackingLimitSiteConfig(
        `stacking_limit,otp_limit,order_status_change_limit,shift_start_wallet_threshold,selfie_restrict_shift_start,restrict_rider_on_register,hide_customer_location_on_app,${WALLET_COMPLIANCE_SLUGS},${RADIUS_COMPLIANCE_SLUGS}`
      );
      setPostData(resp.data.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const targetId = e.target.id;
    const targetMin = e.target.getAttribute("min");
    const targetMax = e.target.getAttribute("max");

    if (targetMax && parseInt(e.target.value) > parseInt(targetMax)) {
      setErrors({ [`${targetId}`]: "Value must be less than " + targetMax });
      setLoading(true);
    } else if (targetMin && parseInt(e.target.value) <= parseInt(targetMin)) {
      setErrors({ [`${targetId}`]: "Value must be greater than " + targetMin });
      setLoading(true);
    } else if (!e.target.value) {
      setErrors({ [`${targetId}`]: "Must have value" });
      setLoading(true);
    } else {
      setErrors({ [`${targetId}`]: false });
      setLoading(false);
    }

    if (targetId == "stacking_limit") {
      setStackingLimit(e.target.value);
    } else if (targetId == "otp_limit") {
      setOtpLimit(e.target.value);
    } else if (targetId == "order_status_change_limit") {
      setOrderStatusChangeLimit(e.target.value);
    } else if (targetId == "shift_start_wallet_threshold") {
      setShiftStartWalletThreshold(e.target.value);
    }
  };

  const switchHandleChange = (e, id) => {
    if (id == "selfie_restrict_shift_start") {
      setSelfieRestrictShiftStart(e);
    } else if (id == "restrict_rider_on_register") {
      setRestrictRiderOnRegister(e);
    } else if (id == "hide_customer_location_on_app") {
      setHideCustomerLocationOnApp(e);
    } else if (id == "wallet_compliance_enforcement_enabled") {
      setWalletComplianceEnabled(e);
    } else if (id == "radius_compliance_enforcement_enabled") {
      setRadiusComplianceEnabled(e);
    } else if (id == "allow_help_center_end") {
      setRadiusAllowHelpCenterEndComplianceEnabled(e);
    } else if (id == "allow_api_end") {
      setRadiusAllowApiEndComplianceEnabled(e);
    }
    
  };

  const updateRuleField = (index, field, value) => {
    setWalletComplianceRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule))
    );
    setErrors((prev) => ({ ...prev, wallet_compliance_rules: false }));
  };

  const addComplianceRule = () => {
    setWalletComplianceRules((prev) => [...prev, createEmptyRule()]);
    setComplianceActiveKeys(["conditions"]);
  };

  const removeComplianceRule = (index) => {
    setWalletComplianceRules((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateClearanceThreshold = (index, value) => {
    setWalletClearanceThresholds((prev) =>
      prev.map((row, i) => (i === index ? { ...row, threshold: value } : row))
    );
    setErrors((prev) => ({ ...prev, wallet_clearance_thresholds: false }));
  };

  const validateClearanceThresholds = (rows) => {
    if (!rows.length) {
      return "Wallet clearance thresholds are required";
    }
    for (let i = 0; i < rows.length; i++) {
      const threshold = Number(rows[i].threshold);
      if (!Number.isFinite(threshold) || threshold < 0) {
        return `Batch ${rows[i].batch}: threshold must be 0 or greater`;
      }
    }
    return null;
  };

  const validateComplianceRules = (rules) => {
    if (!rules.length) {
      return "At least one condition is required";
    }
    const seen = new Set();
    for (let i = 0; i < rules.length; i++) {
      const threshold = parseInt(rules[i].threshold, 10);
      const reason = String(rules[i].reason || "").trim();
      if (!Number.isFinite(threshold) || threshold < 1) {
        return `Condition ${i + 1}: threshold must be >= 1`;
      }
      if (!reason) {
        return `Condition ${i + 1}: reason is required`;
      }
      if (seen.has(threshold)) {
        return `Duplicate threshold ${threshold}. Each condition must have a unique threshold.`;
      }
      seen.add(threshold);
    }
    return null;
  };

  const UpdateFunction = async () => {
    try {
      const validationError = validateComplianceRules(walletComplianceRules);
      if (validationError) {
        setErrors({ wallet_compliance_rules: validationError });
        return;
      }

      const clearanceError = validateClearanceThresholds(walletClearanceThresholds);
      if (clearanceError) {
        setErrors({ wallet_clearance_thresholds: clearanceError });
        return;
      }

      const normalizedRules = walletComplianceRules.map((rule) => ({
        threshold: parseInt(rule.threshold, 10),
        reason: String(rule.reason || "").trim(),
        allow_rider_end: Boolean(rule.allow_rider_end),
        allow_help_center_end: Boolean(rule.allow_help_center_end),
        allow_api_end: Boolean(rule.allow_api_end),
      }));

      const normalizedClearance = walletClearanceThresholds.map((row) => ({
        batch: String(row.batch),
        threshold: Number(row.threshold) || 0,
      }));

      setLoading(true);
      await updateStackingLimitSiteConfig({ slug: "stacking_limit", value: stackingLimit });
      await updateStackingLimitSiteConfig({ slug: "otp_limit", value: otpLimit });
      await updateOrderStatusChangeLimitSiteConfig({
        slug: "order_status_change_limit",
        value: orderStatusChangeLimit,
      });
      await updateShiftStartWalletThresholdSiteConfig({
        slug: "shift_start_wallet_threshold",
        value: shiftStartWalletThreshold,
      });
      await updateSiteConfigCommonFunction({
        slug: "selfie_restrict_shift_start",
        value: selfieRestrictShiftStart,
      });
      await updateSiteConfigCommonFunction({
        slug: "restrict_rider_on_register",
        value: restrictRiderOnRegister,
      });
      await updateSiteConfigCommonFunction({
        slug: "hide_customer_location_on_app",
        value: hideCustomerLocationOnApp,
      });
      await updateSiteConfigCommonFunction({
        slug: "wallet_compliance_enforcement_enabled",
        value: walletComplianceEnabled,
      });
      await updateSiteConfigCommonFunction({
        slug: "radius_compliance_enforcement_enabled",
        value: radiusComplianceEnabled,
      });
      await updateSiteConfigCommonFunction({
        slug: "allow_help_center_end",
        value: radiusAllowHelpCenterEndComplianceEnabled,
      });
      await updateSiteConfigCommonFunction({
        slug: "allow_api_end",
        value: radiusAllowApiEndComplianceEnabled,
      });
      await updateSiteConfigCommonFunction({
        slug: "wallet_compliance_rules",
        value: normalizedRules,
      });
      await updateSiteConfigCommonFunction({
        slug: "wallet_clearance_thresholds",
        value: normalizedClearance,
      });
      setWalletComplianceRules(normalizedRules);
      setWalletClearanceThresholds(normalizedClearance);
      setErrors({ wallet_compliance_rules: false, wallet_clearance_thresholds: false });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="page-content-header">
        <div className="pch">
          <Title level={2}>Site Config</Title>
        </div>
        <div className="pch"></div>
      </div>
      <div className="p-content custom-config">
        <table>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>Order Limit in Cron</Text>
              <Input
                type="number"
                id="stacking_limit"
                placeholder="Order Limit"
                min="1"
                max="10"
                value={stackingLimit}
                onChange={handleChange}
              />
              <Text type="danger">{Errors["stacking_limit"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>OTP Limit</Text>
              <Input
                type="number"
                id="otp_limit"
                placeholder="OTP Limit"
                min="1"
                max="100"
                value={otpLimit}
                onChange={handleChange}
              />
              <Text type="danger">{Errors["otp_limit"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>Order Status Change Limit</Text>
              <Input
                type="number"
                id="order_status_change_limit"
                placeholder="Order Status Change Limit"
                min="1"
                max="100"
                value={orderStatusChangeLimit}
                onChange={handleChange}
              />
              <Text type="danger">{Errors["order_status_change_limit"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>Shift Start Wallet Threshold</Text>
              <Input
                type="number"
                id="shift_start_wallet_threshold"
                placeholder="Shift Start Wallet Threshold"
                min="1"
                max="10000"
                value={shiftStartWalletThreshold}
                onChange={handleChange}
              />
              <Text type="danger">{Errors["shift_start_wallet_threshold"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Restrict Rider to register if profile match: </strong>
                <Switch
                  checked={restrictRiderOnRegister}
                  onChange={(e) => switchHandleChange(e, "restrict_rider_on_register")}
                />
              </Text>
              <Text type="danger">{Errors["restrict_rider_on_register"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Restrict Rider to Start Start if selfie not match: </strong>
                <Switch
                  checked={selfieRestrictShiftStart}
                  onChange={(e) => switchHandleChange(e, "selfie_restrict_shift_start")}
                />
              </Text>
              <Text type="danger">{Errors["selfie_restrict_shift_start"]}</Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Hide customer location on rider app: </strong>
                <Switch
                  checked={hideCustomerLocationOnApp}
                  onChange={(e) => switchHandleChange(e, "hide_customer_location_on_app")}
                />
              </Text>
              <Text type="danger">{Errors["hide_customer_location_on_app"]}</Text>
            </td>
          </tr>

          <tr>
            <td>
              <Title level={4} style={{ marginTop: 24 }}>
                Wallet Compliance Enforcement
              </Title>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Enable Wallet Compliance Enforcement: </strong>
                <Switch
                  checked={walletComplianceEnabled}
                  onChange={(e) =>
                    switchHandleChange(e, "wallet_compliance_enforcement_enabled")
                  }
                />
              </Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Collapse
                activeKey={complianceActiveKeys}
                onChange={(keys) =>
                  setComplianceActiveKeys(Array.isArray(keys) ? keys : [keys])
                }
              >
                <Panel
                  header={`Wallet Compliance Conditions (${walletComplianceRules.length})`}
                  key="conditions"
                >
                  {walletComplianceRules.map((rule, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: 16,
                        paddingBottom: 16,
                        borderBottom:
                          index < walletComplianceRules.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <Text strong>
                          Condition {index + 1}
                          {rule.threshold ? ` — Threshold ${rule.threshold}` : ""}
                          {rule.reason ? ` — ${rule.reason}` : ""}
                        </Text>
                        {walletComplianceRules.length > 1 ? (
                          <Button
                            type="link"
                            danger
                            size="small"
                            onClick={() => removeComplianceRule(index)}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <Text>Wallet Compliance Violation Threshold</Text>
                        <Input
                          type="number"
                          min="1"
                          placeholder="Threshold"
                          value={rule.threshold}
                          onChange={(e) =>
                            updateRuleField(index, "threshold", e.target.value)
                          }
                        />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <Text>Reason</Text>
                        <Input
                          placeholder="Break reason"
                          value={rule.reason}
                          onChange={(e) =>
                            updateRuleField(index, "reason", e.target.value)
                          }
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>
                          <strong>Allow Rider Manual Break Removal: </strong>
                          <Switch
                            checked={Boolean(rule.allow_rider_end)}
                            onChange={(checked) =>
                              updateRuleField(index, "allow_rider_end", checked)
                            }
                          />
                        </Text>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>
                          <strong>Allow Back Office Break Removal: </strong>
                          <Switch
                            checked={Boolean(rule.allow_help_center_end)}
                            onChange={(checked) =>
                              updateRuleField(index, "allow_help_center_end", checked)
                            }
                          />
                        </Text>
                      </div>
                      <div>
                        <Text>
                          <strong>Enable API Based Break Removal: </strong>
                          <Switch
                            checked={Boolean(rule.allow_api_end)}
                            onChange={(checked) =>
                              updateRuleField(index, "allow_api_end", checked)
                            }
                          />
                        </Text>
                      </div>
                    </div>
                  ))}
                  <Button type="dashed" block onClick={addComplianceRule}>
                    Add more condition
                  </Button>
                </Panel>
              </Collapse>
              <Text type="danger">{Errors["wallet_compliance_rules"]}</Text>
            </td>
          </tr>
          
          <tr>
            <td>
              <Title level={4} style={{ marginTop: 24 }}>
                Radius Compliance Enforcement
              </Title>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Enable Radius Compliance Enforcement: </strong>
                <Switch
                  checked={radiusComplianceEnabled}
                  onChange={(e) =>
                    switchHandleChange(e, "radius_compliance_enforcement_enabled")
                  }
                />
              </Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Allow Back Office Break Removal: </strong>
                <Switch
                  checked={radiusAllowHelpCenterEndComplianceEnabled}
                  onChange={(e) =>
                    switchHandleChange(e, "allow_help_center_end")
                  }
                />
              </Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <Text>
                <strong>Enable API Based Break Removal: </strong>
                <Switch
                  checked={radiusAllowApiEndComplianceEnabled}
                  onChange={(e) =>
                    switchHandleChange(e, "allow_api_end")
                  }
                />
              </Text>
            </td>
          </tr>

          <tr>
            <td>
              <Title level={4} style={{ marginTop: 24 }}>
                Configurable Wallet Clearance Threshold
              </Title>
              <Text type="secondary">
                A separate wallet-clearance threshold is configurable batch-wise.
              </Text>
            </td>
          </tr>
          <tr>
            <td className="ant-row ant-form-item">
              <table style={{ width: "100%", maxWidth: 480, marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>Batch</th>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>
                      Wallet Clearance Threshold
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {walletClearanceThresholds.map((row, index) => (
                    <tr key={row.batch}>
                      <td style={{ padding: "6px 12px 6px 0" }}>
                        <Text>Batch {row.batch}</Text>
                      </td>
                      <td style={{ padding: "6px 0" }}>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={row.threshold}
                          onChange={(e) =>
                            updateClearanceThreshold(index, e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Text type="danger">{Errors["wallet_clearance_thresholds"]}</Text>
            </td>
          </tr>

          <tr>
            <td>
              <Button onClick={() => UpdateFunction()} disabled={loading} loading={loading}>
                Update
              </Button>
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};
export default SiteConfig;
